import { RECOVERY_FAILED_MESSAGE } from "./constants";
import { messageOf } from "./isChunkLoadError";
import { getLastReloadAt } from "./reloadGuard";

export type ChunkFailureReason = "import-rejected" | "resolved-undefined";

export interface ChunkFailureContext {
  reason: ChunkFailureReason;
  // The module we were loading, e.g. "../pages/rates/Homepage".
  module?: string;
  cause?: unknown;
}

// Scalars only, so this survives serialization into Sentry's `extra`.
export interface ChunkDiagnostics {
  reason: ChunkFailureReason;
  module: string | null;
  chunkUrl: string | null;
  online: boolean | null;
  msSinceLastReload: number | null;
  causeName: string | null;
  causeMessage: string | null;
}

// Browsers name the unreachable asset in the message, e.g.
// "Failed to fetch dynamically imported module: https://host/vite/assets/Foo-abc.js"
const CHUNK_URL_PATTERN = /(https?:\/\/[^\s)"']+|\/[^\s)"']*assets\/[^\s)"']+)/;

const extractChunkUrl = (message: string): string | null =>
  message.match(CHUNK_URL_PATTERN)?.[0] ?? null;

const isOnline = (): boolean | null =>
  typeof navigator?.onLine === "boolean" ? navigator.onLine : null;

/**
 * A missing asset and a dropped connection produce the same error message, so
 * `online` and `chunkUrl` are what tell them apart in Sentry.
 */
export const buildChunkDiagnostics = (
  context: ChunkFailureContext,
  now: number = Date.now(),
): ChunkDiagnostics => {
  const causeMessage =
    context.cause === undefined ? null : messageOf(context.cause) || null;
  const lastReloadAt = getLastReloadAt();

  return {
    reason: context.reason,
    module: context.module ?? null,
    chunkUrl: causeMessage ? extractChunkUrl(causeMessage) : null,
    online: isOnline(),
    msSinceLastReload: lastReloadAt === 0 ? null : now - lastReloadAt,
    causeName: context.cause instanceof Error ? context.cause.name : null,
    causeMessage,
  };
};

/**
 * Thrown when a chunk fails again after a recovery reload. The message is always
 * the same so Sentry groups these together; the details go in `diagnostics`.
 */
export class ChunkRecoveryError extends Error {
  readonly diagnostics: ChunkDiagnostics | null;

  constructor(diagnostics: ChunkDiagnostics | null, cause?: unknown) {
    super(RECOVERY_FAILED_MESSAGE, { cause });
    this.name = "ChunkRecoveryError";
    this.diagnostics = diagnostics;
  }
}
